const DEFAULT_DELETE_TOKEN = "DELETE";
const NCRM_PROPERTY_LINKABLE_KEYS = new Set(["email_primary", "phone_primary", "website"]);
export const NCRM_CONTACT_LABEL_VALUES = Object.freeze(["work", "personal", "other"]);
const NCRM_CONTACT_LABEL_FIELDS = new Set([
  "email_primary_label",
  "email_secondary_label",
  "email_tertiary_label",
  "phone_primary_label",
  "phone_secondary_label",
  "phone_tertiary_label",
]);

const normalizeString = (value) => String(value ?? "").trim();

export function normalizeDeleteConfirmText(value) {
  return normalizeString(value).toUpperCase();
}

export function isDeleteConfirmValid(value, token = DEFAULT_DELETE_TOKEN) {
  const normalizedToken = normalizeDeleteConfirmText(token || DEFAULT_DELETE_TOKEN);
  if (!normalizedToken) return false;
  return normalizeDeleteConfirmText(value) === normalizedToken;
}

export function resolveNcrmContactsTab(value, fallback = "companies") {
  const normalized = normalizeString(value).toLowerCase();
  if (normalized === "people" || normalized === "companies") {
    return normalized;
  }
  return fallback;
}

export function formatNcrmPersonName(person) {
  const firstName = normalizeString(person?.first_name);
  const lastName = normalizeString(person?.last_name);
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  if (fullName) return fullName;
  return lastName || firstName;
}

const toPayloadString = (value) => normalizeString(value);

export function normalizeNcrmNullableBoolean(value) {
  if (value === null || value === undefined || value === "") return null;
  if (typeof value === "boolean") return value ? 1 : 0;
  if (typeof value === "number") {
    if (!Number.isFinite(value)) return null;
    if (value === 1) return 1;
    if (value === 0) return 0;
    return null;
  }
  const normalized = normalizeString(value).toLowerCase();
  if (["null", "none", "n/a", "na", "-", "--", "unknown", "necunoscut"].includes(normalized)) return null;
  if (["1", "true", "yes", "da", "activ", "active"].includes(normalized)) return 1;
  if (["0", "false", "no", "nu", "inactiv", "inactive"].includes(normalized)) return 0;
  return null;
}

export function normalizeNcrmBusinessActiveSelectValue(value) {
  const normalized = normalizeNcrmNullableBoolean(value);
  if (normalized === 1 || normalized === 0) return normalized;
  return "unknown";
}

export function isNcrmContactLabelField(key) {
  return NCRM_CONTACT_LABEL_FIELDS.has(normalizeString(key));
}

export function buildNcrmContactLabelOptions(translate = (key) => key) {
  return NCRM_CONTACT_LABEL_VALUES.map((value) => ({
    value,
    label: translate(`ncrm.contacts.details.contactLabels.${value}`),
  }));
}

export function buildNcrmCompanyPatchPayload(form = {}) {
  return {
    name: toPayloadString(form.name),
    business_category: toPayloadString(form.business_category),
    business_is_active: normalizeNcrmNullableBoolean(form.business_is_active),
    email_primary: toPayloadString(form.email_primary),
    phone_primary: toPayloadString(form.phone_primary),
    website: toPayloadString(form.website),
    source: toPayloadString(form.source),
    country: toPayloadString(form.country),
    address_text: toPayloadString(form.address_text),
    notes: toPayloadString(form.notes),
  };
}

export function buildNcrmPersonPatchPayload(form = {}) {
  return {
    first_name: toPayloadString(form.first_name),
    last_name: toPayloadString(form.last_name),
    position: toPayloadString(form.position),
    department: toPayloadString(form.department),
    business_is_active: normalizeNcrmNullableBoolean(form.business_is_active),
    email_primary: toPayloadString(form.email_primary),
    phone_primary: toPayloadString(form.phone_primary),
    source: toPayloadString(form.source),
    country: toPayloadString(form.country),
    address_text: toPayloadString(form.address_text),
    notes: toPayloadString(form.notes),
  };
}

export function formatNcrmDateTime(value, emptyLabel = "—") {
  if (!value) return emptyLabel;
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(value);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  const HH = String(date.getHours()).padStart(2, "0");
  const min = String(date.getMinutes()).padStart(2, "0");
  return `${dd}.${mm}.${yyyy}, ${HH}:${min}`;
}

export function formatNcrmDate(value, emptyLabel = "—") {
  if (!value) return emptyLabel;
  const date = new Date(String(value).replace(" ", "T"));
  if (Number.isNaN(date.getTime())) return String(value);
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${dd}.${mm}.${yyyy}`;
}

function sanitizePhoneForHref(value) {
  const normalized = normalizeString(value);
  if (!normalized) return "";
  const cleaned = normalized.replace(/[^+\d]/g, "");
  if (!cleaned || !/\d/.test(cleaned)) return "";
  return cleaned;
}

function sanitizeWebsiteForHref(value) {
  const normalized = normalizeString(value);
  if (!normalized) return "";
  const withProtocol = /^https?:\/\//i.test(normalized) ? normalized : `https://${normalized}`;
  try {
    const parsed = new URL(withProtocol);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return "";
    return parsed.toString();
  } catch (_error) {
    return "";
  }
}

export function isNcrmPropertyLinkable(key) {
  const normalizedKey = normalizeString(key);
  return NCRM_PROPERTY_LINKABLE_KEYS.has(normalizedKey);
}

export function resolveNcrmPropertyLinkMeta(key, value) {
  const normalizedKey = normalizeString(key);
  const normalizedValue = normalizeString(value);
  if (!normalizedValue || !isNcrmPropertyLinkable(normalizedKey)) return null;

  if (normalizedKey === "email_primary") {
    return {
      type: "email",
      href: `mailto:${normalizedValue}`,
      target: "",
      rel: "",
      copyValue: normalizedValue,
    };
  }

  if (normalizedKey === "phone_primary") {
    const phoneHrefValue = sanitizePhoneForHref(normalizedValue);
    if (!phoneHrefValue) return null;
    return {
      type: "phone",
      href: `tel:${phoneHrefValue}`,
      target: "",
      rel: "",
      copyValue: normalizedValue,
    };
  }

  if (normalizedKey === "website") {
    const websiteHref = sanitizeWebsiteForHref(normalizedValue);
    if (!websiteHref) return null;
    return {
      type: "website",
      href: websiteHref,
      target: "_blank",
      rel: "noopener noreferrer",
      copyValue: normalizedValue,
    };
  }

  return null;
}

export function formatNcrmTimestampTooltip(value) {
  if (value === null || value === undefined) return "";
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return "";
    return value.toISOString();
  }
  const normalized = normalizeString(value);
  if (!normalized) return "";
  const parsed = new Date(normalized);
  if (Number.isNaN(parsed.getTime())) return normalized;
  return parsed.toISOString();
}
